<template>
  <div>
    <!-- Reports, downloads and running projects for the processing map (moved from Processes.vue, US 9369) -->

    <!-- Running projects modal -->
    <b-modal ref="running-projects"
        hide-footer
        size="xl"
        @hide="hideProjectsModal"
        modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideProjectsModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="view-running-projects-title">
            {{ $t('projects.in_progress_title') }}
          </h5>
        </div>
      </template>

      <b-form>
        <div class="mt-3">
          <table class="table table-responsive-md table-list">
            <thead>
              <tr>
                <th scope="col">
                  {{ $t('projects.table.name') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.owners') }}
                </th>
                <th scope="col">
                  {{ $t('processes.table.tags') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.type') }}
                </th>
                <th scope="col">
                  {{ $t('projects.table.scope') }}
                </th>
                <th scope="col">
                  {{ $t('projects.create.schedule_label') }}
                </th>
                <th scope="col">
                  {{ $t('system.status') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in projectItems" :key="object.id">
                <td>
                  <router-link
                      :to="{ name: 'projects.view', params: { slug: object.slug }}"
                      qid="s-view-page-link"
                  >
                    {{ object.title }}
                  </router-link>
                </td>
                <td>
                  <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)" :key="user.id">
                    <span qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
                  <span v-if="object.users.length > 3"
                        class="badge cursor-pointer"
                        @click="openUsersModal(object)"
                  >
                    <font-awesome-icon icon="users"/>
                    + {{ object.users.length - 3 }}
                  </span>
                </td>
                <td>
                  <span v-for="tag in getReducedTags(object.tags_translated)" :key="tag.name_translated" qid="p-tags-list">
                    <span class="badge" :title="tag.name_translated">
                      <font-awesome-icon icon="tag"/>
                      {{ tag.name_translated | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.tags_translated.length > 3"
                        class="badge cursor-pointer"
                        @click="openTagsModal(object)"
                  >
                    <font-awesome-icon icon="tags"/>
                    + {{ object.tags_translated.length - 3 }}
                  </span>
                </td>
                <td>
                  <span v-if="object.type == 'update'" class="badge">
                    {{ $t('projects.view.update_label') }}
                  </span>
                  <span v-if="object.type == 'validation'" class="badge">
                    {{ $t('projects.view.validation_label') }}
                  </span>
                  <span v-if="object.type == 'control'" class="badge">
                    {{ $t('projects.view.control_label') }}
                  </span>
                  <span v-if="object.type == 'security_measure'" class="badge">
                    {{ $t('projects.view.security_measure_label') }}
                  </span>
                </td>
                <td>
                  <span class="badge" v-if="object.scope == 'policy'">
                    <font-awesome-icon icon="file-invoice"/>
                    {{ $t('projects.scope.policy') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'system'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.system.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'process'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.process.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'activity'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.activity.label') }}
                  </span>
                  <span class="badge" v-if="object.scope == 'third_party'">
                    <font-awesome-icon icon="crosshairs"/>
                    {{ $t('projects.third_party.label') }}
                  </span>
                </td>
                <td>
                  <div class="line-height-20" v-if="object.control_type == 'continuous'">
                    <span v-if="object.control_type == 'continuous'">
                      {{ $t('projects.schedule.continuous') }}
                    </span>
                  </div>
                  <div class="text-muted line-height-20" v-if="object.control_type == 'continuous'">
                    <span v-if="object.is_active_child_project">{{ $t('system.currently_running') }}</span>
                    <span v-else-if="object.nextRun">{{ $t('projects.schedule.next_run_table_label') }}:  {{ object.nextRun | formatDate }}</span>
                    <span v-else-if="!object.nextRun">{{ $t('system.not_scheduled') }}</span>
                  </div>
                  <div :class="{'line-height-20': object.start_date}" v-if="object.control_type == 'one time'">
                    <span v-if="object.control_type == 'one time'">
                      {{ $t('projects.schedule.one_time') }}
                    </span>
                  </div>
                  <div class="text-muted line-height-20" v-if="object.start_date && object.control_type == 'one time'">
                    {{ object.start_date | formatDate }} - {{ object.end_date | formatDate }}
                  </div>
                </td>
                <td>
                  <span v-if="object.control_type == 'one time'">
                    <span v-if="object.status === 'completed'" class="pr-1">
                      <font-awesome-icon icon="check-circle" class="icon-success"/>
                      {{ $t('system.completed') }}
                    </span>
                    <span v-if="object.status === 'pending'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
                      {{ $t('system.pending') }}
                    </span>
                    <span v-if="object.status === 'in progress'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
                      {{ $t('system.in_progress') }}
                    </span>
                    <span v-if="object.status === 'closed'" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-12"/>
                      {{ $t('system.closed') }}
                    </span>
                  </span>
                  <span v-if="object.control_type == 'continuous'">
                    <span v-if="!object.is_active_child_project" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
                      {{ $t('system.pending') }}
                    </span>
                    <span v-if="object.is_active_child_project" class="pr-1">
                      <font-awesome-icon icon="exclamation-triangle" class="risk-1"/>
                      {{ $t('system.in_progress') }}
                    </span>
                  </span>
                </td>
              </tr>

              <tr v-if="!loading && !projectItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>

              <tr v-if="loading" qid="no-results-row-rr" class="text-center">
                <td colspan="10">
                  <div class="mt-2 mb-4">
                    <div class="swing-container">
                      <div class="swing">
                        <div class="swing-l"></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div class="swing-r"></div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="proxy-footer-xl">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideProjectsModal"
                            qid="view-running-projects-cancel-button"
                  >
                    {{ $t('system.close') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Download modal template -->
    <b-modal ref="download-modal" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('download_modal.title') }}
        </h4>
      </div>
      <div class="mt-2 mb-2">

        <label class="mt-2">
          {{ $t('download_modal.label.select') }}
        </label>

        <div class="row mt-2 mb-2">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.label.select_all') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="selectAll"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.basic_info"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.owners') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.owners"
              :options="radioOptions"
              qid="owners"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('download_modal.value.activities') }}
        </label>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.activities') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.activities"
              :options="radioOptions"
              qid="activities"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.gdprModule">
          {{ $t('download_modal.value.gdpr_module') }}
        </label>

        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.gdpr_basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.gdpr_basic_info"
              :options="radioOptions"
              qid="gdpr_basic_info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.legal_basis') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.legal_basis"
              :options="radioOptions"
              qid="data_subjects"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.legal_basis_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.legal_basis_validation_log"
              :options="radioOptions"
              qid="retention_rules"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.information_obligation') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.information_obligation"
              :options="radioOptions"
              qid="data_subjects"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.information_obligation_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.information_obligation_validation_log"
              :options="radioOptions"
              qid="retention_rule_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_profile_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_profile_validation_log"
              :options="radioOptions"
              qid="integrity_control_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.confidentiality_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.confidentiality_validation_log"
              :options="radioOptions"
              qid="processing_type_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.integrity_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.integrity_validation_log"
              :options="radioOptions"
              qid="processing_type_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.availability_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.availability_validation_log"
              :options="radioOptions"
              qid="processing_type_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.riskModule">
          {{ $t('download_modal.value.risk_control_module') }}
        </label>

        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_scenarios') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_scenarios"
              :options="radioOptions"
              qid="risk_scenarios"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_validation_log"
              :options="radioOptions"
              qid="risk_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeDownloadModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="downloadTable()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
        >
          {{ $t('system.download') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Download modal template -->
    <b-modal ref="download-activities-modal" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('download_modal.title') }}
        </h4>
      </div>
      <div class="mt-2 mb-2">

        <label class="mt-2">
          {{ $t('download_modal.label.select') }}
        </label>

        <div class="row mt-2 mb-2">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.label.select_all') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="selectAllActivities"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.basic_info"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.owners') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.owners"
              :options="radioOptions"
              qid="owners"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('download_modal.value.data_mapping') }}
        </label>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.systems') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.systems"
              :options="radioOptions"
              qid="systems"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.system_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.system_data"
              :options="radioOptions"
              qid="system_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule && getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_sources') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.third_party_sources"
              :options="radioOptions"
              qid="third_party_sources"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule && getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_sources_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.third_party_sources_data"
              :options="radioOptions"
              qid="third_party_sources_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_receivers') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.third_party_receivers"
              :options="radioOptions"
              qid="third_party_receivers"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.contractBasicModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.third_party_receivers_data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.third_party_receivers_data"
              :options="radioOptions"
              qid="third_party_receivers_data"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.data_flow_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.data_flow_validation_log"
              :options="radioOptions"
              qid="data_flow_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.gdprModule">
          {{ $t('activities.measures.title') }}
        </label>

        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('activities.measures.title') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.measures"
              :options="radioOptions"
              qid="measures"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.gdprModule">
          {{ $t('download_modal.value.gdpr_module') }}
        </label>

        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.gdpr_basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.gdpr_basic_info"
              :options="radioOptions"
              qid="gdpr_basic_info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.data_subjects') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.data_subjects"
              :options="radioOptions"
              qid="data_subjects"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.retention_rules') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.retention_rules"
              :options="radioOptions"
              qid="retention_rules"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.retention_rule_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.retention_rule_validation_log"
              :options="radioOptions"
              qid="retention_rule_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.integrity_control_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.integrity_control_validation_log"
              :options="radioOptions"
              qid="integrity_control_validation_log"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.gdprAdvancedModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.processing_type_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.processing_type_validation_log"
              :options="radioOptions"
              qid="processing_type_validation_log"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.riskModule">
          {{ $t('download_modal.value.risk_control_module') }}
        </label>

        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_scenarios') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.risk_scenarios"
              :options="radioOptions"
              qid="risk_scenarios"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadActivitiesForm.risk_validation_log"
              :options="radioOptions"
              qid="risk_validation_log"
            ></b-form-radio-group>
          </div>
        </div>


      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeDownloadActivitiesModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="downloadActivitiesTable()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
        >
          {{ $t('system.download') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Process report modal -->
    <b-modal ref="process-modal" hide-footer @hide="closeProcessModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{$t('reports.process_overview_report')}}
        </h4>
      </div>
      <b-alert v-if="modalError.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ modalError }}
      </b-alert>

      <div class="form-container">
        <b-form-group>
          <label qid="create-ac-pg-label">
            {{$t('reports.modal.scope_type_label')}}
          </label>
          <multiselect
              v-model="selectedScopeType"
              :options="processScopeTypes"
              :placeholder="$t('reports.modal.scope_type_placeholder')"
              @select="resetObjects"
              label="name"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              track-by="value"
              qid="create-rr-form-rr"
          ></multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_owner'">
          <label qid="create-p-users-label">
            {{ $t('processes.create.owners_label') }}
          </label>
          <multiselect
              v-model="selectedUsers"
              :options="users"
              :multiple="true"
              :taggable="false"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              @search-change="loadUsers"
              track-by="slug"
              :placeholder="$t('processes.create.owners_placeholder')"
              label="name"
              qid="create-p-users-select"
          >
            <template slot="tag" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">
                {{ props.option.name }}
              </span>
            </template>
          </multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_group'">
          <label qid="create-p-pg-label">
            {{ $t('processes.create.process_group_label') }}
          </label>
          <multiselect
              v-model="selectedProcessGroups"
              :options="processGroups"
              :multiple="true"
              :taggable="false"
              @search-change="loadProcessGroups"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              :placeholder="$t('processes.create.process_group_placeholder')"
              label="name"
              track-by="slug"
              qid="create-p-form-pg"
          ></multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_name'">
          <label qid="create-p-pg-label">
            {{ $t('reports.modal.process_label') }}
          </label>
          <multiselect
              v-model="selectedProcesses"
              :options="processes"
              :multiple="true"
              :taggable="false"
              @search-change="loadProcesses"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              :placeholder="$t('reports.modal.process_placeholder')"
              label="name"
              track-by="slug"
              qid="create-p-form-pg"
          ></multiselect>
        </b-form-group>
      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeProcessModal"
                  qid="create-rr-cancel-button"
        >
          {{ $t('risk_scenarios.threat.create.cancel') }}
        </b-button>
        <b-button @click="downloadProcessOverviewFile()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="create-rr-submit-button"
        >
          {{ $t('risk_scenarios.threat.create.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Article report modal -->
    <b-modal ref="article-modal" hide-footer @hide="closeArticleModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{$t('reports.article_report')}}
        </h4>
      </div>
      <b-alert v-if="modalError.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ modalError }}
      </b-alert>

      <div class="form-container">

        <b-form-group>
          <label qid="create-ac-pg-label">
            {{ $t('activities.processing_role.title') }}
          </label>
          <multiselect
              v-model="processingRoles"
              :options="processingRoleOptions"
              :multiple="true"
              :placeholder="$t('activities.processing_role.pr_placeholder')"
              label="name"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              track-by="name"
              qid="processing-role-dropdown"
          >
            <template slot="option" slot-scope="props">
              <span v-if="props.option.value == 'Data processor'">
                <font-awesome-icon icon="microchip" class="mr-2"/>
              </span>
              <span v-if="props.option.value == 'Data controller'">
                <font-awesome-icon icon="joystick" class="mr-2"/>
              </span>
              <span v-if="props.option.value == 'Joint controller'">
                <font-awesome-icon icon="handshake" class="mr-2"/>
              </span>
              {{ $t('maintenance.third_parties.processing_role.' + $options.filters.slugify(props.option.value)) }}
            </template>
            <template slot="singleLabel" slot-scope="props">
              <span v-if="props.option.value == 'Data processor'">
                <font-awesome-icon icon="microchip" class="mr-2"/>
              </span>
              <span v-if="props.option.value == 'Data controller'">
                <font-awesome-icon icon="joystick" class="mr-2"/>
              </span>
              <span v-if="props.option.value == 'Joint controller'">
                <font-awesome-icon icon="handshake" class="mr-2"/>
              </span>
              {{ $t('maintenance.third_parties.processing_role.' + $options.filters.slugify(props.option.value)) }}
            </template>
          </multiselect>
        </b-form-group>
        <b-form-group>
          <label qid="create-ac-pg-label">
            {{$t('reports.modal.scope_type_label')}}
          </label>
          <multiselect
              v-model="selectedScopeType"
              :options="processScopeTypes"
              :placeholder="$t('reports.modal.scope_type_placeholder')"
              label="name"
              @select="resetObjects"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              track-by="value"
              qid="create-rr-form-rr"
          ></multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_owner'">
          <label qid="create-p-users-label">
            {{ $t('processes.create.owners_label') }}
          </label>
          <multiselect
              v-model="selectedUsers"
              :options="users"
              :multiple="true"
              :taggable="false"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              @search-change="loadUsers"
              track-by="slug"
              :placeholder="$t('processes.create.owners_placeholder')"
              label="name"
              qid="create-p-users-select"
          >
            <template slot="tag" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">
                {{ props.option.name }}
              </span>
            </template>
          </multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_group'">
          <label qid="create-p-pg-label">
            {{ $t('processes.create.process_group_label') }}
          </label>
          <multiselect
              v-model="selectedProcessGroups"
              :options="processGroups"
              :multiple="true"
              :taggable="false"
              @search-change="loadProcessGroups"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              :placeholder="$t('processes.create.process_group_placeholder')"
              label="name"
              track-by="slug"
              qid="create-p-form-pg"
          ></multiselect>
        </b-form-group>
        <b-form-group v-if="selectedScopeType && selectedScopeType.value && selectedScopeType.value == 'process_name'">
          <label qid="create-p-pg-label">
            {{ $t('reports.modal.process_label') }}
          </label>
          <multiselect
              v-model="selectedProcesses"
              :options="processes"
              :multiple="true"
              :taggable="false"
              @search-change="loadProcesses"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              :placeholder="$t('reports.modal.process_placeholder')"
              label="name"
              track-by="slug"
              qid="create-p-form-pg"
          ></multiselect>
        </b-form-group>
      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeArticleModal"
                  qid="create-rr-cancel-button"
        >
          {{ $t('risk_scenarios.threat.create.cancel') }}
        </b-button>
        <b-button @click="downloadArticleFile()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="create-rr-submit-button"
        >
          {{ $t('risk_scenarios.threat.create.submit') }}
        </b-button>
      </div>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';

  /**
   * The processing map opens these through a ref: openRunningProjects(), openDownloadProcesses(),
   * openDownloadActivities(), openProcessModal() and openArticleModal()
   */
  export default {
    name: 'ProcessListActions',
    components: {
      Avatar,
      ShowMoreModal,
    },
    data: function() {
      return {
        projectItems: [],
        loading: false,
        selectedItem: {},
        showTagsModal: false,
        showUsersModal: false,
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        selectAll: '',
        selectAllActivities: '',
        downloadForm: {
          availability_validation_log: 0,
          basic_info: 1,
          confidentiality_validation_log: 0,
          gdpr_basic_info: 0,
          information_obligation: 0,
          information_obligation_validation_log: 0,
          integrity_validation_log: 0,
          legal_basis: 0,
          legal_basis_validation_log: 0,
          owners: 0,
          risk_profile_validation_log: 0,
          risk_scenarios: 0,
          risk_validation_log: 0,
          activities: 0
        },
        downloadActivitiesForm: {
          basic_info: 1,
          owners: 0,
          systems: 0,
          system_data: 0,
          third_party_sources: 0,
          third_party_sources_data: 0,
          third_party_receivers: 0,
          third_party_receivers_data: 0,
          data_flow_validation_log: 0,
          gdpr_basic_info: 0,
          data_subjects: 0,
          retention_rules: 0,
          retention_rule_validation_log: 0,
          integrity_control_validation_log: 0,
          processing_type_validation_log: 0,
          risk_scenarios: 0,
          risk_validation_log: 0,
          measures: 0,
          client: {}
        },
        users: [],
        selectedUsers: [],
        processes: [],
        processGroups: [],
        selectedProcesses: [],
        selectedProcessGroups: [],
        processingRoleOptions: [
          { text: this.$t('maintenance.third_parties.processing_role.data_controller'), value: 'Data controller'},
          { text: this.$t('maintenance.third_parties.processing_role.data_processor'), value: 'Data processor'},
          { text: this.$t('activities.processing_role.joint_controller_label'), value: 'Joint controller' }
        ],
        processScopeTypes: [
          { name: this.$t('reports.scope_types.process_owner'), value: 'process_owner' },
          { name: this.$t('reports.scope_types.process_name'), value: 'process_name' },
          { name: this.$t('reports.scope_types.process_group'), value: 'process_group' },
        ],
        processingRoles: [],
        selectedScopeType: [],
        modalError: '',
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
    },
    watch: {
      selectAll: function (value) {
        this.downloadForm.basic_info = value
        this.downloadForm.owners = value

        if (this.getClient.gdprModule) {
          this.downloadForm.legal_basis = value
          this.downloadForm.legal_basis_validation_log = value
          this.downloadForm.information_obligation = value
          this.downloadForm.information_obligation_validation_log = value
          this.downloadForm.risk_profile_validation_log = value
          this.downloadForm.confidentiality_validation_log = value
          this.downloadForm.integrity_validation_log = value
          this.downloadForm.availability_validation_log = value
          this.downloadForm.gdpr_basic_info = value
        }

        if (this.getClient.riskModule) {
          this.downloadForm.risk_scenarios = value
          this.downloadForm.risk_validation_log = value
        }

        this.downloadForm.activities = value
      },
      selectAllActivities: function (value) {
        this.downloadActivitiesForm.basic_info = value
        this.downloadActivitiesForm.owners = value
        this.downloadActivitiesForm.systems = value
        this.downloadActivitiesForm.system_data = value

        if (this.getClient.contractBasicModule) {
          this.downloadActivitiesForm.third_party_sources = value
          this.downloadActivitiesForm.third_party_sources_data = value
          this.downloadActivitiesForm.third_party_receivers = value
          this.downloadActivitiesForm.third_party_receivers_data = value
        }

        this.downloadActivitiesForm.data_flow_validation_log = value

        if (this.getClient.gdprModule) {
          this.downloadActivitiesForm.gdpr_basic_info = value
          this.downloadActivitiesForm.data_subjects = value
          this.downloadActivitiesForm.retention_rules = value
          this.downloadActivitiesForm.retention_rule_validation_log = value
          this.downloadActivitiesForm.integrity_control_validation_log = value
          this.downloadActivitiesForm.processing_type_validation_log = value
        }

        if (this.getClient.riskModule) {
          this.downloadActivitiesForm.risk_scenarios = value
          this.downloadActivitiesForm.risk_validation_log = value
        }

        this.downloadActivitiesForm.measures = value
      }
    },
    methods: {
      openRunningProjects() {
        this.loadProjects()
        this.$refs['running-projects'].show()
      },
      hideProjectsModal() {
        this.$refs['running-projects'].hide()
      },
      openDownloadProcesses() {
        this.$refs['download-modal'].show()
      },
      closeDownloadModal() {
        this.$refs['download-modal'].hide()
      },
      openDownloadActivities() {
        this.$refs['download-activities-modal'].show()
      },
      closeDownloadActivitiesModal() {
        this.$refs['download-activities-modal'].hide()
      },
      resetObjects() {
        this.selectedProcesses = []
        this.selectedProcessGroups = []
        this.selectedUsers = []
      },
      openArticleModal() {
        this.selectedScopeType = []
        this.resetObjects()
        this.loadProcessGroups();
        this.loadProcesses();
        this.loadUsers();
        this.$refs['article-modal'].show()
      },
      closeArticleModal() {
        this.modalError = ''
        this.$refs['article-modal'].hide()
      },
      openProcessModal() {
        this.selectedScopeType = []
        this.resetObjects()
        this.loadProcessGroups();
        this.loadProcesses();
        this.loadUsers();
        this.$refs['process-modal'].show()
      },
      closeProcessModal() {
        this.modalError = ''
        this.$refs['process-modal'].hide()
      },
      ids(items, key) {
        return (items || []).map(function(item) {
          return item[key || 'id'];
        });
      },
      // The scope type needs a selection, unless no scope type is chosen
      scopeIsComplete() {
        let type = this.selectedScopeType ? this.selectedScopeType.value : null;
        if (!type) {
          return true;
        }

        return (type == 'process_owner' && this.selectedUsers && this.selectedUsers.length) ||
          (type == 'process_name' && this.selectedProcesses && this.selectedProcesses.length) ||
          (type == 'process_group' && this.selectedProcessGroups && this.selectedProcessGroups.length);
      },
      scopeError() {
        let type = this.selectedScopeType ? this.selectedScopeType.value : null;
        if (type == 'process_owner') {
          this.modalError = this.$t('reports.owners_required');
        }
        if (type == 'process_name') {
          this.modalError = this.$t('reports.process_required');
        }
        if (type == 'process_group') {
          this.modalError = this.$t('reports.process_group_required');
        }
      },
      requestReport(url, params, ref) {
        let self = this;
        piincHttp.get(url, { params: params }).then(function(response) {
          if (response.status == 200) {
            self.$toastr('success', self.$t('reports.success_message'))
          }
          self.$refs[ref].hide()
        }, function() {
          self.modalError = self.$t('reports.something_went_wrong');
        });
      },
      downloadArticleFile() {
        if (!this.scopeIsComplete()) {
          return this.scopeError();
        }

        this.requestReport('processes/generate-reports', {
          users: this.ids(this.selectedUsers),
          processes: this.ids(this.selectedProcesses),
          process_groups: this.ids(this.selectedProcessGroups),
          client: this.getClient.slug,
          processing_roles: this.ids(this.processingRoles, 'value')
        }, 'article-modal');
      },
      downloadProcessOverviewFile() {
        if (!this.scopeIsComplete()) {
          return this.scopeError();
        }

        this.requestReport('processes/generate-reports/overview', {
          users: this.ids(this.selectedUsers),
          processes: this.ids(this.selectedProcesses),
          process_groups: this.ids(this.selectedProcessGroups),
          client: this.getClient.slug
        }, 'process-modal');
      },
      downloadTable() {
        let self = this;
        this.downloadForm.client = this.getClient.slug;
        piincHttp.post('processes/download', this.downloadForm).then(function() {
          self.closeDownloadModal();
          self.$toastr('success', self.$t('exports.success_message'))
        }, function() {});
      },
      downloadActivitiesTable() {
        let self = this;
        this.downloadActivitiesForm.client = this.getClient.slug;
        piincHttp.post('activities/download', this.downloadActivitiesForm).then(function() {
          self.closeDownloadActivitiesModal();
          self.$toastr('success', self.$t('exports.success_message'))
        }, function() {});
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      getReducedTags(tags) {
        return tags.length > 3 ? tags.slice(0, 3) : tags;
      },
      getReducedUsers(users) {
        return users.length > 3 ? users.slice(0, 3) : users;
      },
      loadProjects() {
        this.loading = true;
        let self = this;
        piincHttp.get('projects', { params:
          {
            per_page: 50,
            status: ['in progress', 'pending'],
            scopes: ['process', 'activity'],
            without_parent: 1,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.projectItems = response.data.items;
        }, function() {});
      },
      loadProcesses(query) {
        let self = this;
        piincHttp.get('processes', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.processes = response.data.items
          if (self.processes && !self.processes.length && !query) {
            self.processes.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadProcessGroups() {
        let self = this;
        piincHttp.get('process-groups', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.processGroups = response.data.items
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 10
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    }
  }
</script>
